import React from 'react';
import { Typography, Grid, Box, Accordion, AccordionSummary, AccordionDetails, Card } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const UseCasesSection = () => {
  return (
    <section id="usecases" className="whitepaper-section">
      <Typography variant="h2" className="section-title">
        Real-World Use Cases
      </Typography>

      <Accordion 
        defaultExpanded
        sx={{ 
          background: 'linear-gradient(135deg, rgba(96,165,250,0.1) 0%, rgba(59,130,246,0.05) 100%)',
          border: '2px solid rgba(96,165,250,0.3)',
          borderRadius: '16px !important',
          mb: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          '&:before': { display: 'none' },
          overflow: 'hidden'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ color: '#60a5fa', fontSize: '2rem' }} />}
          sx={{
            background: 'rgba(96,165,250,0.15)',
            borderBottom: '1px solid rgba(96,165,250,0.2)',
            minHeight: '80px',
            '&:hover': { background: 'rgba(96,165,250,0.2)' },
            transition: 'background 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(96,165,250,0.4)'
            }}>
              <CloudIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#60a5fa', fontSize: '1.4rem' }}>
              1. Cloud SIEM Migration & Hybrid Analytics
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 4, background: 'rgba(10,34,64,0.3)' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#60a5fa', fontWeight: 700, fontSize: '1.1rem' }}>Challenge:</Box> Global financial services firm running Splunk Enterprise ($18M/year) needed to adopt Google Chronicle for cloud workloads without losing on-premises visibility.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#10b981', fontWeight: 700, fontSize: '1.1rem' }}>Solution:</Box> Deployed Confluent as central data fabric, sending filtered data to both platforms.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#f59e0b', fontWeight: 700, fontSize: '1.15rem' }}>
              Results:
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
                border: '2px solid rgba(96,165,250,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(96,165,250,0.3)',
                  borderColor: 'rgba(96,165,250,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(96,165,250,0.4)'
                }}>
                  <TrendingDownIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#60a5fa', mb: 1 }}>80%</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Cost Reduction</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
                border: '2px solid rgba(16,185,129,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(16,185,129,0.3)',
                  borderColor: 'rgba(16,185,129,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
                }}>
                  <AccessTimeIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#10b981', mb: 1 }}>6 months</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Migration Time</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(217,119,6,0.1) 100%)',
                border: '2px solid rgba(245,158,11,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(245,158,11,0.3)',
                  borderColor: 'rgba(245,158,11,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(245,158,11,0.4)'
                }}>
                  <CheckCircleIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#f59e0b', mb: 1 }}>Zero</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Detection Gaps</Typography>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion 
        sx={{ 
          background: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.05) 100%)',
          border: '2px solid rgba(16,185,129,0.3)',
          borderRadius: '16px !important',
          mb: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          '&:before': { display: 'none' },
          overflow: 'hidden'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ color: '#10b981', fontSize: '2rem' }} />}
          sx={{
            background: 'rgba(16,185,129,0.15)',
            borderBottom: '1px solid rgba(16,185,129,0.2)',
            minHeight: '80px',
            '&:hover': { background: 'rgba(16,185,129,0.2)' },
            transition: 'background 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(16,185,129,0.4)'
            }}>
              <SecurityIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981', fontSize: '1.4rem' }}>
              2. Ransomware Detection & Response
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 4, background: 'rgba(10,34,64,0.3)' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#10b981', fontWeight: 700, fontSize: '1.1rem' }}>Challenge:</Box> Healthcare network needed faster detection of ransomware lateral movement (30+ minutes with batch SIEM ingestion).
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#60a5fa', fontWeight: 700, fontSize: '1.1rem' }}>Solution:</Box> Real-time correlation in Kafka Streams analyzing EDR + network + file activity patterns.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#f59e0b', fontWeight: 700, fontSize: '1.15rem' }}>
              Results:
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
                border: '2px solid rgba(16,185,129,0.3)',
                borderRadius: '16px',
                p: 2.5,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(16,185,129,0.3)',
                  borderColor: 'rgba(16,185,129,0.5)'
                }
              }}>
                <Box sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
                }}>
                  <SpeedIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#10b981', mb: 0.5 }}>3 min</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>Detection Time (vs 30min)</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
                border: '2px solid rgba(96,165,250,0.3)',
                borderRadius: '16px',
                p: 2.5,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(96,165,250,0.3)',
                  borderColor: 'rgba(96,165,250,0.5)'
                }
              }}>
                <Box sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  boxShadow: '0 8px 24px rgba(96,165,250,0.4)'
                }}>
                  <TrendingDownIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#60a5fa', mb: 0.5 }}>92%</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>False Positive Reduction</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(217,119,6,0.1) 100%)',
                border: '2px solid rgba(245,158,11,0.3)',
                borderRadius: '16px',
                p: 2.5,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(245,158,11,0.3)',
                  borderColor: 'rgba(245,158,11,0.5)'
                }
              }}>
                <Box sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  boxShadow: '0 8px 24px rgba(245,158,11,0.4)'
                }}>
                  <AccessTimeIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#f59e0b', mb: 0.5 }}>15 sec</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>Automated Response</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.1) 100%)',
                border: '2px solid rgba(239,68,68,0.3)',
                borderRadius: '16px',
                p: 2.5,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(239,68,68,0.3)',
                  borderColor: 'rgba(239,68,68,0.5)'
                }
              }}>
                <Box sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  boxShadow: '0 8px 24px rgba(239,68,68,0.4)'
                }}>
                  <AttachMoneyIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#ef4444', mb: 0.5 }}>$8.2M</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>Breach Cost Avoidance</Typography>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion 
        sx={{ 
          background: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(217,119,6,0.05) 100%)',
          border: '2px solid rgba(245,158,11,0.3)',
          borderRadius: '16px !important',
          mb: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          '&:before': { display: 'none' },
          overflow: 'hidden'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ color: '#f59e0b', fontSize: '2rem' }} />}
          sx={{
            background: 'rgba(245,158,11,0.15)',
            borderBottom: '1px solid rgba(245,158,11,0.2)',
            minHeight: '80px',
            '&:hover': { background: 'rgba(245,158,11,0.2)' },
            transition: 'background 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(245,158,11,0.4)'
            }}>
              <PersonSearchIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b', fontSize: '1.4rem' }}>
              3. Insider Threat Detection
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 4, background: 'rgba(10,34,64,0.3)' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '1.1rem' }}>Challenge:</Box> Technology company needed behavioral analytics across 50+ data sources to detect insider threats.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#60a5fa', fontWeight: 700, fontSize: '1.1rem' }}>Solution:</Box> Confluent aggregating user activity streams with ML-based anomaly detection using Kafka Streams and Flink.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#10b981', fontWeight: 700, fontSize: '1.15rem' }}>
              Results:
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(217,119,6,0.1) 100%)',
                border: '2px solid rgba(245,158,11,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(245,158,11,0.3)',
                  borderColor: 'rgba(245,158,11,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(245,158,11,0.4)'
                }}>
                  <CheckCircleIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#f59e0b', mb: 1 }}>93%</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Detection Accuracy</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
                border: '2px solid rgba(16,185,129,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(16,185,129,0.3)',
                  borderColor: 'rgba(16,185,129,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
                }}>
                  <AccessTimeIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#10b981', mb: 1 }}>10 min</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Alert Generation</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
                border: '2px solid rgba(96,165,250,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(96,165,250,0.3)',
                  borderColor: 'rgba(96,165,250,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(96,165,250,0.4)'
                }}>
                  <TrendingDownIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#60a5fa', mb: 1 }}>78%</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Investigation Time Reduction</Typography>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion 
        sx={{ 
          background: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(124,58,237,0.05) 100%)',
          border: '2px solid rgba(139,92,246,0.3)',
          borderRadius: '16px !important',
          mb: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          '&:before': { display: 'none' },
          overflow: 'hidden'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ color: '#8b5cf6', fontSize: '2rem' }} />}
          sx={{
            background: 'rgba(139,92,246,0.15)',
            borderBottom: '1px solid rgba(139,92,246,0.2)',
            minHeight: '80px',
            '&:hover': { background: 'rgba(139,92,246,0.2)' },
            transition: 'background 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(139,92,246,0.4)'
            }}>
              <PrecisionManufacturingIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#8b5cf6', fontSize: '1.4rem' }}>
              4. OT/ICS Security Monitoring
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 4, background: 'rgba(10,34,64,0.3)' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#8b5cf6', fontWeight: 700, fontSize: '1.1rem' }}>Challenge:</Box> Manufacturing company needed unified visibility across IT and OT networks without impacting production systems.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#10b981', fontWeight: 700, fontSize: '1.1rem' }}>Solution:</Box> Confluent as air-gapped data diode collecting OT telemetry for analysis in IT security tools.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#f59e0b', fontWeight: 700, fontSize: '1.15rem' }}>
              Results:
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(124,58,237,0.1) 100%)',
                border: '2px solid rgba(139,92,246,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(139,92,246,0.3)',
                  borderColor: 'rgba(139,92,246,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(139,92,246,0.4)'
                }}>
                  <CheckCircleIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#8b5cf6', mb: 1 }}>Zero</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>OT Network Impact</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
                border: '2px solid rgba(16,185,129,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(16,185,129,0.3)',
                  borderColor: 'rgba(16,185,129,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
                }}>
                  <CheckCircleIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#10b981', mb: 1 }}>100%</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>ICS Protocol Visibility</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
                border: '2px solid rgba(96,165,250,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(96,165,250,0.3)',
                  borderColor: 'rgba(96,165,250,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(96,165,250,0.4)'
                }}>
                  <VerifiedUserIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#60a5fa', mb: 1 }}>IEC 62443</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Compliance Achieved</Typography>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion 
        sx={{ 
          background: 'linear-gradient(135deg, rgba(236,72,153,0.1) 0%, rgba(219,39,119,0.05) 100%)',
          border: '2px solid rgba(236,72,153,0.3)',
          borderRadius: '16px !important',
          mb: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          '&:before': { display: 'none' },
          overflow: 'hidden'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon sx={{ color: '#ec4899', fontSize: '2rem' }} />}
          sx={{
            background: 'rgba(236,72,153,0.15)',
            borderBottom: '1px solid rgba(236,72,153,0.2)',
            minHeight: '80px',
            '&:hover': { background: 'rgba(236,72,153,0.2)' },
            transition: 'background 0.3s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: '50px',
              height: '50px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(236,72,153,0.4)'
            }}>
              <VerifiedUserIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#ec4899', fontSize: '1.4rem' }}>
              5. Zero Trust Architecture
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 4, background: 'rgba(10,34,64,0.3)' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#ec4899', fontWeight: 700, fontSize: '1.1rem' }}>Challenge:</Box> SaaS provider needed real-time access decisions based on continuous authentication and device posture.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5, color: '#e0e7ef', lineHeight: 1.8, fontSize: '1.05rem' }}>
              <Box component="span" sx={{ color: '#10b981', fontWeight: 700, fontSize: '1.1rem' }}>Solution:</Box> Confluent streaming identity, device, and threat context to policy enforcement points with &lt;10ms latency.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: '#f59e0b', fontWeight: 700, fontSize: '1.15rem' }}>
              Results:
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(236,72,153,0.15) 0%, rgba(219,39,119,0.1) 100%)',
                border: '2px solid rgba(236,72,153,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(236,72,153,0.3)',
                  borderColor: 'rgba(236,72,153,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(236,72,153,0.4)'
                }}>
                  <SpeedIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#ec4899', mb: 1 }}>&lt;10ms</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Policy Decision Latency</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
                border: '2px solid rgba(96,165,250,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(96,165,250,0.3)',
                  borderColor: 'rgba(96,165,250,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(96,165,250,0.4)'
                }}>
                  <CheckCircleIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#60a5fa', mb: 1 }}>500K</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Decisions/Second</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
                border: '2px solid rgba(16,185,129,0.3)',
                borderRadius: '16px',
                p: 3,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(16,185,129,0.3)',
                  borderColor: 'rgba(16,185,129,0.5)'
                }
              }}>
                <Box sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: '0 8px 24px rgba(16,185,129,0.4)'
                }}>
                  <SecurityIcon sx={{ color: '#fff', fontSize: '2rem' }} />
                </Box>
                <Typography sx={{ fontSize: '2.5rem', fontWeight: 800, color: '#10b981', mb: 1 }}>85%</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '1rem', fontWeight: 600 }}>Lateral Movement Blocked</Typography>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </section>
  );
};

export default UseCasesSection;
